import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { UserPublic, Organization, TokenPair } from '../types/auth';
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  storeTokens,
  clearTokens,
  getMe,
  refreshTokens,
  logout as logoutApi,
  getOriginTokens,
  storeOriginTokens,
  clearOriginTokens,
  readImpersonation,
} from '../services/auth';

interface AuthState {
  user: UserPublic | null;
  organization: Organization | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  loginSuccess: (user: UserPublic, organization: Organization | null, tokens: TokenPair) => void;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  /**
   * Refresh the cached user/org after the user edits their own profile,
   * so the sidebar (name, email) updates without a reload. Tokens are
   * untouched -- auth keys off userId, not the email in the token.
   */
  updateUser: (user: UserPublic, organization: Organization | null) => void;
  /**
   * Non-null iff the CURRENT session was minted by a super_admin via
   * "Log in as customer". Read straight off the access token, which the
   * backend re-stamps on every refresh — so this can't go stale mid-session.
   */
  impersonation: { impersonatorEmail: string } | null;
  /** Swap into a customer's session, parking our own tokens for the return trip. */
  startImpersonation: (user: UserPublic, organization: Organization | null, tokens: TokenPair) => void;
  /** Swap back to the super_admin session we parked. */
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {throw new Error('useAuth must be used within AuthProvider');}
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    organization: null,
    accessToken: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const refreshTimerRef = useRef<number | null>(null);

  // Schedule token refresh 1 minute before expiry (14 min for 15 min tokens)
  const scheduleRefresh = useCallback((token: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    // Parse JWT to get exp
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresIn = (payload.exp * 1000) - Date.now() - 60000; // 1 min before expiry
      if (expiresIn > 0) {
        refreshTimerRef.current = window.setTimeout(async () => {
          try {
            const newTokens = await refreshTokens();
            setState(prev => ({ ...prev, accessToken: newTokens.accessToken }));
            scheduleRefresh(newTokens.accessToken);
          } catch {
            // Refresh failed, force logout
            setState({
              user: null,
              organization: null,
              accessToken: null,
              isLoading: false,
              isAuthenticated: false,
            });
            clearTokens();
          }
        }, expiresIn);
      }
    } catch {
      // Invalid token
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    async function init() {
      const accessToken = getStoredAccessToken();
      const refreshToken = getStoredRefreshToken();

      if (!accessToken && !refreshToken) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        // Try current access token first
        if (accessToken) {
          const { user, organization } = await getMe(accessToken);
          setState({
            user,
            organization,
            accessToken,
            isLoading: false,
            isAuthenticated: true,
          });
          scheduleRefresh(accessToken);
          return;
        }
      } catch {
        // Access token expired, try refresh
      }

      try {
        if (refreshToken) {
          const newTokens = await refreshTokens();
          const { user, organization } = await getMe(newTokens.accessToken);
          setState({
            user,
            organization,
            accessToken: newTokens.accessToken,
            isLoading: false,
            isAuthenticated: true,
          });
          scheduleRefresh(newTokens.accessToken);
          return;
        }
      } catch {
        // Refresh failed
      }

      clearTokens();
      setState(prev => ({ ...prev, isLoading: false }));
    }

    void init();
  }, [scheduleRefresh]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const loginSuccess = useCallback((user: UserPublic, organization: Organization | null, tokens: TokenPair) => {
    storeTokens(tokens);
    setState({
      user,
      organization,
      accessToken: tokens.accessToken,
      isLoading: false,
      isAuthenticated: true,
    });
    scheduleRefresh(tokens.accessToken);
  }, [scheduleRefresh]);

  const updateUser = useCallback((user: UserPublic, organization: Organization | null) => {
    setState((prev) => ({ ...prev, user, organization }));
  }, []);

  const logout = useCallback(async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    await logoutApi();
    setState({
      user: null,
      organization: null,
      accessToken: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  // Enter a customer's session. We park our own tokens FIRST — if the swap
  // somehow fails halfway, the origin key is already on disk and the banner's
  // Exit button can still get us home.
  const startImpersonation = useCallback((
    user: UserPublic,
    organization: Organization | null,
    tokens: TokenPair
  ) => {
    const current = {
      accessToken: getStoredAccessToken() || '',
      refreshToken: getStoredRefreshToken() || '',
    };
    if (current.accessToken && current.refreshToken) {
      storeOriginTokens(current);
    }
    storeTokens(tokens);
    setState({
      user,
      organization,
      accessToken: tokens.accessToken,
      isLoading: false,
      isAuthenticated: true,
    });
    scheduleRefresh(tokens.accessToken);
  }, [scheduleRefresh]);

  // Return to our own account. Note we do NOT call the logout endpoint on the
  // impersonated session — leaving the row in `sessions` is what preserves the
  // audit trail. It expires on its own.
  const stopImpersonation = useCallback(async () => {
    const origin = getOriginTokens();
    if (!origin) {
      // Nothing to go back to (origin tokens lost/expired). Safest exit is a
      // full logout rather than stranding someone inside a customer account.
      await logoutApi();
      setState({
        user: null,
        organization: null,
        accessToken: null,
        isLoading: false,
        isAuthenticated: false,
      });
      return;
    }

    clearOriginTokens();
    storeTokens(origin);

    try {
      const { user, organization } = await getMe(origin.accessToken);
      setState({
        user,
        organization,
        accessToken: origin.accessToken,
        isLoading: false,
        isAuthenticated: true,
      });
      scheduleRefresh(origin.accessToken);
    } catch {
      // Our parked access token went stale while we were away (likely — we
      // were gone longer than 15 min). The refresh token outlives it, so
      // rotate it back into a fresh pair rather than forcing a re-login.
      try {
        const fresh = await refreshTokens();
        const { user, organization } = await getMe(fresh.accessToken);
        setState({
          user,
          organization,
          accessToken: fresh.accessToken,
          isLoading: false,
          isAuthenticated: true,
        });
        scheduleRefresh(fresh.accessToken);
      } catch {
        clearTokens();
        setState({
          user: null,
          organization: null,
          accessToken: null,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    }
  }, [scheduleRefresh]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (state.accessToken) {
      // Check if token is about to expire (within 30 seconds)
      try {
        const payload = JSON.parse(atob(state.accessToken.split('.')[1]));
        if ((payload.exp * 1000) - Date.now() > 30000) {
          return state.accessToken;
        }
      } catch {
        // Fall through to refresh
      }
    }

    // Try to refresh
    try {
      const newTokens = await refreshTokens();
      setState(prev => ({ ...prev, accessToken: newTokens.accessToken }));
      scheduleRefresh(newTokens.accessToken);
      return newTokens.accessToken;
    } catch {
      await logout();
      return null;
    }
  }, [state.accessToken, scheduleRefresh, logout]);

  return (
    <AuthContext.Provider value={{
      ...state,
      loginSuccess,
      logout,
      getToken,
      updateUser,
      impersonation: readImpersonation(state.accessToken),
      startImpersonation,
      stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
